import React, { useState, useEffect } from "react";
import CustomModal from "../../components/CustomModal";
import { useExtendReservation } from "./hooks";
import { AdminDeal } from "./api";
import { useI18n } from "../../i18n";

interface ExtendReservationModalProps {
  open: boolean;
  onClose: () => void;
  deal: AdminDeal | null;
}

const PRESETS = [3, 6, 12];

const formatDate = (d: Date, locale: string) =>
  d.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const ExtendReservationModal: React.FC<ExtendReservationModalProps> = ({
  open,
  onClose,
  deal,
}) => {
  const { t, code } = useI18n();
  const [months, setMonths] = useState<number>(6);
  const extendMutation = useExtendReservation();

  useEffect(() => {
    if (open) setMonths(6);
  }, [open, deal]);

  if (!deal) return null;

  // Current expiry: backend value, or computed created + 6mo as a fallback
  const currentExpiry = deal.expiresAt
    ? new Date(deal.expiresAt)
    : (() => {
        const d = new Date(deal.createdAt);
        d.setMonth(d.getMonth() + 6);
        return d;
      })();

  const newExpiry = new Date(currentExpiry);
  newExpiry.setMonth(newExpiry.getMonth() + months);

  const handleExtend = async () => {
    try {
      await extendMutation.mutateAsync({ uuid: deal.uuid, months });
      onClose();
    } catch {
      // error toast handled in the hook
    }
  };

  return (
    <CustomModal open={open} onClose={onClose}>
      <div className="w-[520px] max-w-full">
        <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
          <div
            className="flex items-center justify-center w-10 h-10 rounded-lg"
            style={{ backgroundColor: "#7E3AF220" }}
          >
            <i
              className="fa-solid fa-calendar-plus text-xl"
              style={{ color: "#7E3AF2" }}
            ></i>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t("deals.extend.title")}
            </h2>
            <p className="text-sm text-gray-400">
              {deal.companyName} · {t("deals.extend.vat", { vat: deal.vatNumber })}
            </p>
          </div>
        </div>

        <div className="py-5 space-y-5">
          {/* Current expiry */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">{t("deals.extend.currentExpiration")}</span>
            <span className="font-medium text-gray-800">
              {formatDate(currentExpiry, code)}
            </span>
          </div>

          {/* Extend by */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">
              {t("deals.extend.extendBy")}
            </label>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMonths(m)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    months === m
                      ? "bg-[#7E3AF2] text-white border-[#7E3AF2]"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {t("deals.extend.plusMonths", { months: m })}
                </button>
              ))}
              <div className="flex items-center gap-1.5 border border-gray-300 rounded-lg px-3">
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={months}
                  onChange={(e) =>
                    setMonths(
                      Math.max(1, Math.min(60, Number(e.target.value) || 1))
                    )
                  }
                  className="w-14 py-2 text-sm text-gray-700 focus:outline-none"
                />
                <span className="text-sm text-gray-400">{t("deals.extend.monthsShort")}</span>
              </div>
            </div>
          </div>

          {/* New expiry preview */}
          <div className="flex items-center justify-between rounded-lg bg-green-50 border border-green-200 px-4 py-3">
            <span className="text-sm text-green-700">{t("deals.extend.newExpiration")}</span>
            <span className="font-semibold text-green-800">
              {formatDate(newExpiry, code)}
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            className="px-5 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium"
            onClick={onClose}
            disabled={extendMutation.isPending}
          >
            {t("common.cancel")}
          </button>
          <button
            className="px-5 py-2 rounded-lg text-white font-medium flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: "#7E3AF2" }}
            onClick={handleExtend}
            disabled={extendMutation.isPending}
          >
            {extendMutation.isPending ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                {t("deals.extend.submitting")}
              </>
            ) : (
              <>
                <i className="fa-solid fa-calendar-plus"></i>
                {t("deals.extend.title")}
              </>
            )}
          </button>
        </div>
      </div>
    </CustomModal>
  );
};

export default ExtendReservationModal;
